<script setup>
/** DataTableToolbar.vue - Para ScheduleCampaignDeliveries com Status WhatsApp */
import { computed, ref } from 'vue';
// Usando os componentes shadcn
import { Button } from 'dashboard/components-shadcn/new-york/ui/button';
import { Input } from 'dashboard/components-shadcn/new-york/ui/input';
// Componente de filtro facetado
import DataTableFacetedFilter from './DataTableFacetedFilter.vue';

const props = defineProps({
  table: { type: Object, required: true },
  deliveryStats: { type: Object, required: true },
  templatePricingCategory: { type: Object, required: true },
  isLoading: { type: Boolean, default: false },
  hideCostCard: { type: Boolean, default: false },
});

const emit = defineEmits(['quickFilter', 'clearFilters', 'search', 'statusFilter']);

// Estado local
const searchTerm = ref('');
const selectedStatus = ref('all');

// Função para calcular porcentagens
const getPercentage = (value, total) => {
  if (total === 0) return '0';
  return ((value / total) * 100).toFixed(1);
};

// Cálculo de custo estimado usando template_pricing_category
const estimatedCost = computed(() => {
  let totalCostReais = 0;
  const categoryCosts = {
    marketing: 0,
    utility: 0,
    service: 0,
    authentication: 0
  };
  const categoryCounts = {
    marketing: 0,
    utility: 0,
    service: 0,
    authentication: 0
  };
  
  // Usa os preços do template_pricing_category (que vem em reais)
  const pricingCosts = props.templatePricingCategory;
  
  
  props.table.getCoreRowModel().flatRows.forEach(row => {
    const delivery = row.original;
   
    
    if (delivery.billable === true) {
      let category = delivery.pricing_category;
      
      // Se não tem pricing_category definido, assume marketing como padrão
      if (!category || !pricingCosts[category]) {
        category = 'marketing';

      }
      
      const categoryPrice = pricingCosts[category];
      categoryCounts[category] += 1;
      categoryCosts[category] += categoryPrice;
      totalCostReais += categoryPrice;
      
    }
  });
  
  const totalMessages = Object.values(categoryCounts).reduce((sum, count) => sum + count, 0);
  const mainCategory = totalMessages > 0 
    ? Object.keys(categoryCounts).reduce((a, b) => categoryCounts[a] > categoryCounts[b] ? a : b)
    : 'marketing';
  
  
  return {
    totalReais: totalCostReais.toFixed(2),
    categoryCounts,
    categoryCosts: {
      marketing: categoryCosts.marketing.toFixed(2),
      utility: categoryCosts.utility.toFixed(2),
      service: categoryCosts.service.toFixed(2),
      authentication: categoryCosts.authentication.toFixed(2)
    },
    mainCategory,
    totalMessages
  };
});

// Opções para filtro facetado
const statusFacetedOptions = computed(() => [
  { value: 'sent', label: 'Enviado' },
  { value: 'delivered', label: 'Nao Lida' },
  { value: 'read', label: 'Lida' },
  { value: 'failed', label: 'Falhou' },
]);

// Se há filtros ativos
const isFiltered = computed(() =>
  searchTerm.value ||
  selectedStatus.value !== 'all'
);

// ✅ Busca global - agora emite evento para server-side
const onSearchChange = (value) => {
  searchTerm.value = value;
  emit('search', value);
};

// ✅ Filtro de status - agora emite evento para server-side
const onStatusChange = (status) => {
  selectedStatus.value = status;
  if (status === 'all') {
    emit('statusFilter', []);
  } else {
    emit('statusFilter', [status]);
  }
  emit('quickFilter', status);
};

// ✅ Limpar filtros
const clearAllFilters = () => {
  searchTerm.value = '';
  selectedStatus.value = 'all';
  emit('search', '');
  emit('statusFilter', []);
  emit('clearFilters');
};

// Filtro rápido
const quickFilter = (status) => {
  onStatusChange(status);
};


</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="space-y-4">
    <!-- Stats Cards Clicáveis com Status WhatsApp -->
    <div class="stats-grid" :class="hideCostCard ? 'stats-grid-5' : 'stats-grid-6'">
      <!-- Card Total - Neutro -->
      <div 
        class="stat-card total-card cursor-pointer hover:scale-105 transition-all duration-200 bg-woot-900"
        @click="quickFilter('all')"
        :class="{ 
          'ring-2 ring-n-iris-9 shadow-md': selectedStatus === 'all',
          'hover:shadow-lg': selectedStatus !== 'all'
        }"
      >
        <div class="stat-icon">
          <span class="i-lucide-mail w-5 h-5 text-n-slate-11"></span>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ deliveryStats.total }}</div>
          <div class="stat-label">Total</div>
        </div>
      </div>
      
      <!-- Card Enviadas - Amarelo com 1 check -->
      <div 
        class="stat-card sent-card cursor-pointer hover:scale-105 transition-all duration-200"
        @click="quickFilter('sent')"
        :class="{ 
          'ring-2 ring-n-amber-9 shadow-md': selectedStatus === 'sent',
          'hover:shadow-lg': selectedStatus !== 'sent'
        }"
      >
        <div class="stat-icon">
          <span class="whatsapp-check-single">✓</span>
        </div>
        <div class="stat-content">
          <div class="stat-number text-n-amber-12">{{ deliveryStats.sent }}</div>
          <div class="stat-label">Enviadas</div>
        </div>
        <div class="stat-badge success" v-if="deliveryStats.total > 0">
          <span class="badge-text">{{ getPercentage(deliveryStats.sent, deliveryStats.total) }}%</span>
        </div>
      </div>
      
      <!-- Card Entregues - Cinza com 2 checks -->
      <div 
        class="stat-card delivered-card cursor-pointer hover:scale-105 transition-all duration-200"
        @click="quickFilter('delivered')"
        :class="{ 
          'ring-2 ring-n-slate-9 shadow-md': selectedStatus === 'delivered',
          'hover:shadow-lg': selectedStatus !== 'delivered'
        }"
      >
        <div class="stat-icon">
          <span class="whatsapp-check-double">✓✓</span>
        </div>
        <div class="stat-content">
          <div class="stat-number text-n-slate-12">{{ deliveryStats.delivered }}</div>
          <div class="stat-label">não lidas</div>
        </div>
        <div class="stat-badge info" v-if="deliveryStats.total > 0">
          <span class="badge-text">{{ getPercentage(deliveryStats.delivered, deliveryStats.total) }}%</span>
        </div>
      </div>
      
      <!-- Card Lidas - Azul com 2 checks azuis -->
      <div 
        class="stat-card read-card cursor-pointer hover:scale-105 transition-all duration-200"
        @click="quickFilter('read')"
        :class="{ 
          'ring-2 ring-n-blue-9 shadow-md': selectedStatus === 'read',
          'hover:shadow-lg': selectedStatus !== 'read'
        }"
      >
        <div class="stat-icon">
          <span class="whatsapp-check-read">✓✓</span>
        </div>
        <div class="stat-content">
          <div class="stat-number text-n-blue-12">{{ deliveryStats.read }}</div>
          <div class="stat-label">Lidas</div>
        </div>
        <div class="stat-badge success" v-if="deliveryStats.total > 0">
          <span class="badge-text">{{ getPercentage(deliveryStats.read, deliveryStats.total) }}%</span>
        </div>
      </div>
      
      <!-- Card Falhas - Vermelho -->
      <div 
        class="stat-card failed-card cursor-pointer hover:scale-105 transition-all duration-200"
        @click="quickFilter('failed')"
        :class="{ 
          'ring-2 ring-n-ruby-9 shadow-md': selectedStatus === 'failed',
          'hover:shadow-lg': selectedStatus !== 'failed'
        }"
      >
        <div class="stat-icon">
          <span class="i-lucide-x-circle w-5 h-5 text-n-ruby-11"></span>
        </div>
        <div class="stat-content">
          <div class="stat-number text-n-ruby-12">{{ deliveryStats.failed }}</div>
          <div class="stat-label">Falhas</div>
        </div>
        <div class="stat-badge danger" v-if="deliveryStats.total > 0">
          <span class="badge-text">{{ getPercentage(deliveryStats.failed, deliveryStats.total) }}%</span>
        </div>
      </div>
      
      <!-- Card Custo Estimado - Iris com breakdown melhorado incluindo custo unitário -->
      <div v-if="!hideCostCard" class="stat-card cost-card relative overflow-hidden group">
        <div class="stat-icon">
          <span class="i-lucide-receipt w-5 h-5 text-n-iris-11"></span>
        </div>
        <div class="stat-content">
          <div class="stat-number text-n-iris-12">
            R$ {{ estimatedCost.totalReais }}
          </div>
          <div class="stat-label">Custo Est.</div>
        </div>
        
        <!-- Overlay com breakdown detalhado no hover -->
        <div class="cost-overlay" v-if="estimatedCost.totalMessages > 0">
          <div class="cost-breakdown">
            <div class="cost-summary">
              <span class="cost-total">R$ {{ estimatedCost.totalReais }}</span>
            </div>
            <div class="cost-details">
              <div class="text-xs text-n-iris-10 space-y-1">
                <div v-if="estimatedCost.categoryCounts.marketing > 0" class="flex justify-between">
                  <span>Marketing ({{ estimatedCost.categoryCounts.marketing }}):</span>
                  <span>R$ {{ estimatedCost.categoryCosts.marketing }}</span>
                </div>
                <div v-if="estimatedCost.categoryCounts.marketing > 0" class="flex justify-between text-n-iris-9 text-xs">
                  <span class="ml-2">→ R$ {{ templatePricingCategory.marketing.toFixed(3) }}/msg</span>
                </div>
                
                <div v-if="estimatedCost.categoryCounts.utility > 0" class="flex justify-between">
                  <span>Utility ({{ estimatedCost.categoryCounts.utility }}):</span>
                  <span>R$ {{ estimatedCost.categoryCosts.utility }}</span>
                </div>
                <div v-if="estimatedCost.categoryCounts.utility > 0" class="flex justify-between text-n-iris-9 text-xs">
                  <span class="ml-2">→ R$ {{ templatePricingCategory.utility.toFixed(3) }}/msg</span>
                </div>
                
                <div v-if="estimatedCost.categoryCounts.service > 0" class="flex justify-between">
                  <span>Serviço ({{ estimatedCost.categoryCounts.service }}):</span>
                  <span>R$ {{ estimatedCost.categoryCosts.service }}</span>
                </div>
                <div v-if="estimatedCost.categoryCounts.service > 0" class="flex justify-between text-n-iris-9 text-xs">
                  <span class="ml-2">→ R$ {{ templatePricingCategory.service.toFixed(3) }}/msg</span>
                </div>
                
                <div v-if="estimatedCost.categoryCounts.authentication > 0" class="flex justify-between">
                  <span>Auth ({{ estimatedCost.categoryCounts.authentication }}):</span>
                  <span>R$ {{ estimatedCost.categoryCosts.authentication }}</span>
                </div>
                <div v-if="estimatedCost.categoryCounts.authentication > 0" class="flex justify-between text-n-iris-9 text-xs">
                  <span class="ml-2">→ R$ {{ templatePricingCategory.authentication.toFixed(3) }}/msg</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Overlay quando não há mensagens faturáveis -->
        <div class="cost-overlay" v-else>
          <div class="cost-breakdown">
            <div class="cost-summary">
              <span class="cost-total">R$ 0.00</span>
              <span class="cost-category-label">Sem custos</span>
            </div>
          </div>
        </div>
      </div>
      
    </div>

    <!-- Toolbar Principal -->
    <div class="flex items-center justify-between bg-n-slate-1 dark:bg-n-slate-2 p-4 rounded-lg border border-n-slate-6 shadow-sm">
      <div class="flex flex-1 items-center space-x-3">
        <!-- Campo de busca global -->
        <div class="relative">
          <span class="absolute left-3 top-1/2 transform -translate-y-1/2 i-lucide-search w-4 h-4 text-n-slate-10"></span>
          <Input
            placeholder="Buscar por contato, telefone, status ou erro..."
            :model-value="searchTerm"
            class="h-9 w-[280px] pl-10 border-n-slate-6 focus:border-n-iris-9 focus:ring-n-iris-9"
            @input="onSearchChange($event.target.value)"
          />
        </div>

        <!-- Filtro Facetado de Status -->
        <DataTableFacetedFilter
          v-if="table.getColumn('status')"
          :column="table.getColumn('status')"
          title="Status"
          :options="statusFacetedOptions"
        />

        <!-- Botão Limpar Filtros -->
        <Button
          v-if="isFiltered"
          variant="outline"
          size="sm"
          @click="clearAllFilters"
          class="h-9 px-3 bg-n-ruby-8 text-n-ruby-11 border-n-ruby-7 hover:bg-n-ruby-4 hover:border-n-ruby-8 transition-colors"
        >
          <span class="i-lucide-x w-4 h-4 mr-1"></span>
          Limpar
        </Button>
      </div>

      <!-- Indicador de loading -->
      <div v-if="isLoading" class="flex items-center gap-2 text-sm text-n-slate-10">
        <div class="w-4 h-4 border-2 border-n-iris-9 border-t-transparent rounded-full animate-spin"></div>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Filtros Ativos (se houver) -->
    <div v-if="isFiltered" class="flex flex-wrap items-center gap-2 px-1">
      <span class="text-xs font-medium text-n-slate-10">Filtros ativos:</span>

      <!-- Filtro global -->
      <div
        v-if="searchTerm"
        class="inline-flex items-center gap-1 px-2 py-1 bg-n-iris-2 text-n-iris-11 text-xs rounded-full border border-n-iris-7"
      >
        <span class="i-lucide-search w-3 h-3"></span>
        <span>{{ searchTerm }}</span>
        <button
          @click="onSearchChange('')"
          class="hover:bg-n-iris-3 rounded-full p-0.5 transition-colors"
        >
          <span class="i-lucide-x w-3 h-3"></span>
        </button>
      </div>

      <!-- Filtro de status -->
      <div
        v-if="selectedStatus !== 'all'"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors"
        :class="{
          'bg-n-amber-2 text-n-amber-11 border-n-amber-7': selectedStatus === 'sent',
          'bg-n-slate-2 text-n-slate-11 border-n-slate-7': selectedStatus === 'delivered',
          'bg-n-blue-2 text-n-blue-11 border-n-blue-7': selectedStatus === 'read',
          'bg-n-ruby-2 text-n-ruby-11 border-n-ruby-7': selectedStatus === 'failed'
        }"
      >
        <span class="text-xs">
          <span v-if="selectedStatus === 'sent'">✓</span>
          <span v-else-if="selectedStatus === 'delivered'">✓✓</span>
          <span v-else-if="selectedStatus === 'read'" class="text-n-blue-9">✓✓</span>
          <span v-else-if="selectedStatus === 'failed'" class="i-lucide-x-circle w-3 h-3"></span>
        </span>
        <span>Status: {{ selectedStatus === 'sent' ? 'Enviado' : selectedStatus === 'delivered' ? 'Não Lida' : selectedStatus === 'read' ? 'Lida' : 'Falhou' }}</span>
        <button
          @click="onStatusChange('all')"
          class="rounded-full p-0.5 transition-colors"
          :class="{
            'hover:bg-n-amber-3': selectedStatus === 'sent',
            'hover:bg-n-slate-3': selectedStatus === 'delivered',
            'hover:bg-n-blue-3': selectedStatus === 'read',
            'hover:bg-n-ruby-3': selectedStatus === 'failed'
          }"
        >
          <span class="i-lucide-x w-3 h-3"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Todos os estilos CSS permanecem iguais - apenas copiando */
/* Grid System - Cards ocupam TODO o espaço disponível */
.stats-grid {
  @apply grid gap-3 w-full;
}

.stats-grid-6 {
  @apply grid-cols-6 max-xl:grid-cols-3 max-md:grid-cols-2;
}

.stats-grid-5 {
  @apply grid-cols-5 max-xl:grid-cols-3 max-md:grid-cols-2;
}


@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }
}

/* NOVA ESTRUTURA DOS CARDS */
.stat-card {
  @apply rounded-lg p-4 flex items-center transition-all duration-200 border backdrop-blur-sm h-[88px] w-full min-w-0 flex-[1_1_0%] gap-3;
}


/* Ícone - posição fixa à esquerda */
.stat-icon {
  @apply flex-shrink-0 flex items-center justify-center w-10 h-10 min-w-[40px];
}


/* Conteúdo central - expande para ocupar espaço restante */
.stat-content {
  @apply flex flex-col flex-1 min-w-0 overflow-hidden justify-center;
}


/* Badge (%) - posição fixa à direita */
.stat-badge {
  @apply flex-shrink-0 flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium border h-6 min-w-[48px] self-center;
}


/* Card Styles */
.total-card {
  @apply bg-n-slate-5 border-n-slate-8 shadow-xl shadow-n-slate-9/25
         dark:bg-n-slate-1 dark:border-n-slate-6 dark:shadow-xl;
}

.sent-card {
  @apply bg-n-amber-5 border-n-amber-8 
         hover:shadow-xl hover:shadow-n-slate-9/25 transition-shadow duration-200
         dark:bg-gradient-to-br dark:from-n-amber-1 dark:to-n-amber-2 dark:border-n-amber-6 dark:hover:shadow-xl;
}

.delivered-card {
  @apply bg-n-slate-4 border-n-slate-7 
         hover:shadow-xl hover:shadow-n-slate-9/25 transition-shadow duration-200
         dark:bg-gradient-to-br dark:from-n-slate-1 dark:to-n-slate-2 dark:border-n-slate-6 dark:hover:shadow-xl;
}

.read-card {
  @apply bg-n-blue-5 border-n-blue-8 
         hover:shadow-xl hover:shadow-n-slate-9/25 transition-shadow duration-200
         dark:bg-gradient-to-br dark:from-n-blue-1 dark:to-n-blue-2 dark:border-n-blue-6 dark:hover:shadow-xl;
}

.failed-card {
  @apply bg-n-ruby-5 border-n-ruby-8 
         hover:shadow-xl hover:shadow-n-slate-9/25 transition-shadow duration-200
         dark:bg-gradient-to-br dark:from-n-ruby-1 dark:to-n-ruby-2 dark:border-n-ruby-6 dark:hover:shadow-xl;
}

.cost-card {
  @apply bg-n-iris-5 border-n-iris-8 
         hover:shadow-xl hover:shadow-n-slate-9/25 transition-shadow duration-200
         dark:bg-gradient-to-br dark:from-n-iris-1 dark:to-n-iris-2 dark:border-n-iris-6 dark:hover:shadow-xl;
}

/* Ícones WhatsApp personalizados */
.whatsapp-check-single {
  @apply text-n-amber-10 font-bold text-lg;
}

.whatsapp-check-double {
  @apply text-n-slate-10 font-bold text-lg;
}

.whatsapp-check-read {
  @apply text-n-blue-9 font-bold text-lg;
}

/* Overlay do breakdown de custo */
.cost-overlay {
  @apply absolute inset-0 bg-gradient-to-br from-n-iris-2/95 to-n-iris-3/95 backdrop-blur-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-center p-3;
}

.cost-breakdown {
  @apply space-y-2 text-center;
}

.cost-summary {
  @apply flex flex-col items-center gap-1;
}

.cost-total {
  @apply text-lg font-bold text-n-iris-12;
}

.cost-category-label {
  @apply text-xs font-medium text-n-iris-11 uppercase tracking-wide;
}

.cost-details {
  @apply flex justify-center;
}

/* Elementos dos Cards */
.stat-number {
  @apply text-lg font-bold leading-none whitespace-nowrap overflow-hidden text-ellipsis;
}


.stat-label {
  @apply text-xs text-n-slate-10 mt-1 font-medium whitespace-nowrap overflow-hidden text-ellipsis;
}


/* Estilos dos badges por tipo */
.stat-badge.success {
  @apply bg-n-teal-2 text-n-teal-11 border-n-teal-7;
}

.stat-badge.info {
  @apply bg-n-slate-2 text-n-slate-11 border-n-slate-6;
}

.stat-badge.danger {
  @apply bg-n-ruby-2 text-n-ruby-11 border-n-ruby-7;
}

.badge-text {
  @apply text-xs font-medium;
}

/* Hover Effects */
.cursor-pointer:hover {
  @apply scale-105 shadow-lg;
}

.total-card:hover {
  @apply border-n-slate-7;
}

.sent-card:hover {
  @apply border-n-amber-7 shadow-n-amber-9/20;
}

.delivered-card:hover {
  @apply border-n-slate-7 shadow-n-slate-9/20;
}

.read-card:hover {
  @apply border-n-blue-7 shadow-n-blue-9/20;
}

.failed-card:hover {
  @apply border-n-ruby-7 shadow-n-ruby-9/20;
}

.cost-card:hover {
  @apply border-n-iris-7 shadow-n-iris-9/20;
}

/* Ring animations */
@keyframes pulse-iris {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--iris-9), 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(var(--iris-9), 0); }
}

@keyframes pulse-warning {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--amber-9), 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(var(--amber-9), 0); }
}

@keyframes pulse-slate {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--slate-9), 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(var(--slate-9), 0); }
}

@keyframes pulse-blue {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--blue-9), 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(var(--blue-9), 0); }
}

@keyframes pulse-error {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--ruby-9), 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(var(--ruby-9), 0); }
}

.ring-2.ring-n-iris-9 {
  animation: pulse-iris 2s infinite;
}

.ring-2.ring-n-amber-9 {
  animation: pulse-warning 2s infinite;
}

.ring-2.ring-n-slate-9 {
  animation: pulse-slate 2s infinite;
}

.ring-2.ring-n-blue-9 {
  animation: pulse-blue 2s infinite;
}

.ring-2.ring-n-ruby-9 {
  animation: pulse-error 2s infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .stat-card {
    height: 80px;
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .stat-icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }
  
  .stat-badge {
    height: 20px;
    min-width: 40px;
    padding: 0.25rem 0.5rem;
  }
  
  .stat-number {
    @apply text-base;
  }
}

@media (max-width: 480px) {
  .stat-card {
    flex-direction: column;
    height: auto;
    min-height: 72px;
    padding: 0.75rem;
    text-align: center;
  }
  
  .stat-icon {
    width: 24px;
    height: 24px;
    min-width: 24px;
  }
  
  .stat-content {
    margin: 0.25rem 0;
  }
  
  .stat-badge {
    height: 18px;
    min-width: 32px;
    align-self: center;
  }
}

/* Position badges absolutely on larger screens */
@media (min-width: 481px) {
  .stat-badge {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
  }
  
  .stat-card {
    position: relative;
    padding-right: 4rem;
  }
  
  @media (max-width: 640px) {
    .stat-card {
      padding-right: 3.5rem;
    }
    
    .stat-badge {
      right: 0.75rem;
    }
  }
}
</style>